// De widgettypes van de rapportage per deelnemer, en de instellingen die de
// openbare link ervan leest. Nodig omdat de widgets in de bouwer (T6–T9) en de
// server (functions/liveReports/personaWidgetTypes.js) dezelfde namen moeten
// gebruiken: een typo in `Type` geeft op de link geen fout maar een lege widget.
//
// DIT IS DE BRON. De server heeft een spiegel; personaWidgetTypes.parity.test.js
// in functions/ wordt rood zodra ze uiteenlopen. Repareer dan de spiegel.
// AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
// AI-CONTEXT: See docs/features/livereport-per-deelnemer.md

/**
 * @typedef {object} PersonaProfileField
 * @property {string} label - Naam van het veld zoals de widget hem toont
 * @property {string} source - Veld op het `Personas`-doc
 */

export const PERSONA_WIDGET_TYPES = Object.freeze({
    /** Aanwezigheid van de deelnemer; geen instellingen behalve `period`. */
    PRESENCE: 'PERSONA_PRESENCE',
    /** Doelen met hun gekoppelde effecten; geen instellingen. */
    GOALS: 'PERSONA_GOALS',
    /** Score per meetmoment; `Settings.researchId` (+ `byNestedId[fieldId].visible`), als de onderzoekwidget. */
    GROWTH: 'PERSONA_GROWTH',
    /** Traject en gegevens; `Settings.fields`: sleutels uit PERSONA_PROFILE_FIXED_FIELDS of `data:<Title>`. */
    PROFILE: 'PERSONA_PROFILE',
    /** Participatieladder: begintrede → huidige trede; geen instellingen. */
    LADDER: 'PERSONA_LADDER',
    /** Arbeidsvaardigheden: groei per thema, eerste → laatste beoordeling; geen instellingen. */
    WORK_SKILLS: 'PERSONA_WORK_SKILLS',
})

/**
 * De vaste velden van 'Traject en gegevens', op sleutel. `Route` wordt via
 * `RouteID` de naam van het traject (`PersonaRoutes.Title`).
 * @type {Readonly<Object<string, PersonaProfileField>>}
 */
export const PERSONA_PROFILE_FIXED_FIELDS = Object.freeze({
    Name: { label: 'Naam', source: 'Name' },
    Status: { label: 'Status', source: 'Status' },
    Route: { label: 'Traject', source: 'RouteID' },
    StartDate: { label: 'Startdatum', source: 'StartDate' },
    EndDate: { label: 'Einddatum', source: 'EndDate' },
    HoursPerWeek: { label: 'Uren per week', source: 'HoursPerWeek' },
})

/** Voorvoegsel voor een `PersonaData`-gegeven in `Settings.fields`: `data:Woonplaats`
 *  (op `Title`, omdat `FormFieldID` bij vrijwel geen gegeven gevuld is). */
export const PERSONA_DATA_FIELD_PREFIX = 'data:'

// Wettelijke identificatienummers (BSN, paspoort, IBAN, …) vallen onder
// strengere regels en horen niet in een rapportage, ook niet als de
// organisatie ze als gegeven bewaart. Besluit 2026-10-20 na een gebruikerstest.
// De bouwer biedt ze niet aan, de widget toont ze niet en de server stuurt ze
// nooit naar de openbare link — ook niet als een oude widget ze nog kiest.
// Titels zijn vrije tekst, dus genormaliseerd: kleine letters, zonder accenten,
// woorden aan elkaar ('B.S.N.' → 'bsn', 'bsn-nummer' → 'bsnnummer').
// Bewust streng: ook een ja/nee- of datumveld over zo'n document ('Paspoort
// aanwezig') en 'Klant ID nummer' vallen weg — liever te veel dan één lek.
// Herkend op de NAAM van het gegeven; een BSN onder 'Nummer' ziet dit niet.
// Varianten gemeten in productie (2026-10-20): 'BSN', 'BSN nummer',
// 'IBAN nummer', 'Documentnummer' (het nummer van het ID-bewijs).

/** Verboden zodra een woord hiermee BEGINT (kort, dus niet midden in een woord: 'Libanon', 'Lidnummer'). */
const FORBIDDEN_WORD_STARTS = [
    'bsn', 'iban', 'sofinummer', 'sofinr', 'idkaart', 'idcard', 'idnummer', 'idnr', 'idbewijs',
    'vnummer', 'vnr', 'anummer',
]
/** Verboden zodra de titel dit ergens bevat (lang genoeg om niet toevallig voor te komen). */
const FORBIDDEN_PARTS = [
    'burgerservicenummer', 'burgerservicenr', 'sofinummer', 'socialsecurity',
    'paspoort', 'passport', 'identiteitsbewijs', 'identiteitskaart', 'identiteitsnummer', 'identificatie',
    'legitimatie', 'verblijfsvergunning', 'vreemdelingennummer', 'rijbewijsnummer', 'rijbewijsnr',
    'rekeningnummer', 'rekeningnr', 'bankrekening', 'bankaccount', 'documentnummer', 'documentnr',
]

/**
 * @param {*} title - `PersonaData.Title`
 * @returns {boolean} true als de titel een wettelijk identificatienummer is
 */
export const isForbiddenPersonaDataTitle = (title) => {
    if (typeof title !== 'string') return false
    const words = title.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean)
    const compact = words.join('')
    if (FORBIDDEN_PARTS.some((part) => compact.includes(part))) return true
    // Elk woord met alles wat erna komt: 'ID kaart' → 'idkaart', 'Uw BSN' → 'bsn'.
    return words.some((_, i) => {
        const tail = words.slice(i).join('')
        return FORBIDDEN_WORD_STARTS.some((start) => tail.startsWith(start))
    })
}

const personaWidgetTypes = { PERSONA_WIDGET_TYPES, PERSONA_PROFILE_FIXED_FIELDS, PERSONA_DATA_FIELD_PREFIX, isForbiddenPersonaDataTitle }

export default personaWidgetTypes
